import React from 'react'
import {cx} from '@emotion/css'

interface PropsWithClassName {
    className?: string
}

/**
 * Extracts optional "className" property from optional "props" and merges it with "defaultClassName".
 *
 * @param defaultClassName Class name which should be returned regardless if "className" was extracted from "props".
 * @param props Component's properties, with optional "className" property.
 * @returns Tuple [merged class name, props without "className" property].
 */
export function useExtractedClassName<Props extends PropsWithClassName>(
    defaultClassName: string,
    props?: Props
): [string, Omit<Props, 'className'> | undefined] {
    let extractedClassName: string | undefined = undefined
    let restProps: Omit<Props, 'className'> | undefined = undefined
    if (props) {
        const {className, ...rest} = props
        extractedClassName = className
        restProps = rest
    }

    const finalClassName = React.useMemo(() => {
        if (extractedClassName) {
            return cx([defaultClassName, extractedClassName])
        }
        return defaultClassName
    }, [defaultClassName, extractedClassName])

    return [finalClassName, restProps]
}
